import { Injectable } from '@angular/core';
import { IAccount } from '@modeso/types__aumico-lib-annualreport-be';
import {
    GetTemplatesNamesAndIdsDto,
    GetTranslationsDto,
    IGroupEntityDTO,
    ITemplateEntityDTO,
    TemplateType,
    UpdateTranslationsDto,
} from '@modeso/types__aumico-lib-templates-be';
import { Store, select } from '@ngrx/store';
import Debug from 'debug';
import { Observable } from 'rxjs';
import * as fromTemplatesActions from '../actions/templates.actions';
import { IFinancialLineItem } from '../interfaces/settings.interface';
import { TemplatesTranslationRequest } from '../interfaces/templates-translation-request.interface';
import * as fromTemplatesReducer from '../reducers/aumico-templates.reducer';
import { AppState } from '../reducers/aumico-templates.reducer';
import { ITemplatesProvider } from './templates.provider.interface';

const debug = Debug('aumico:aumico-lib-templates-fe:TemplatesProvider');

@Injectable({
    providedIn: 'root',
})
export class TemplatesProvider implements ITemplatesProvider {
    constructor(private store: Store<AppState>) {
        debug('Provider constructed');
    }
    setAllAccountsFromExcel(accounts: IAccount[]): void {
        this.store.dispatch(fromTemplatesActions.allAccounts.set({ payload: accounts }));
    }
    addAccountToAllAccountsExcel(account: IAccount): void {
        this.store.dispatch(fromTemplatesActions.allAccounts.addaccount({ payload: account }));
    }
    updateAccountInExcelAccounts(updatedAccount: IAccount, oldAccountNumber: string): void {
        this.store.dispatch(
            fromTemplatesActions.allAccounts.updateAccount({ payload: { updatedAccount, oldAccountNumber } }),
        );
    }

    //Groups
    dispatchGroups() {
        this.store.dispatch(fromTemplatesActions.getGroups());
    }

    getGroups$(): Observable<IGroupEntityDTO[]> {
        return this.store.pipe(select(fromTemplatesReducer.selectGroups));
    }

    getDefaultGroups$(): Observable<Map<string, IGroupEntityDTO>> {
        return this.store.pipe(select(fromTemplatesReducer.selectDefaultGroups));
    }

    getActiveGroup$(): Observable<IGroupEntityDTO> {
        return this.store.pipe(select(fromTemplatesReducer.selectActiveGroup));
    }

    setActiveGroup(groupId: string) {
        this.store.dispatch(fromTemplatesActions.setActiveGroup({ payload: groupId }));
    }

    removeActiveGroup() {
        this.store.dispatch(fromTemplatesActions.removeActiveGroup());
    }

    createGroup(groupToBeCreated: IGroupEntityDTO) {
        this.store.dispatch(fromTemplatesActions.createGroup({ group: groupToBeCreated }));
    }

    updateGroup(groupTobeUpdated: IGroupEntityDTO, groupId: string) {
        this.store.dispatch(fromTemplatesActions.updateGroup({ group: groupTobeUpdated, groupId }));
    }

    deleteGroup(groupId: string) {
        this.store.dispatch(fromTemplatesActions.deleteGroup({ payload: groupId }));
    }

    selectGroups(groupsIds: string[]): void {
        this.store.dispatch(fromTemplatesActions.selectGroups({ payload: groupsIds }));
    }

    deselectGroup(groupId: string): void {
        this.store.dispatch(fromTemplatesActions.deselectGroup({ payload: groupId }));
    }

    getGroupsResponse$(): Observable<any> {
        return this.store.pipe(select(fromTemplatesReducer.selectGroupsResponse));
    }

    removeGroupsResponse() {
        this.store.dispatch(fromTemplatesActions.removeGroupsResponse());
    }

    getGroupsError$(): Observable<any> {
        return this.store.pipe(select(fromTemplatesReducer.selectGroupsError));
    }

    removeGroupsError() {
        this.store.dispatch(fromTemplatesActions.removeGroupsError());
    }

    getGroupNameExists$(name: string, groupId: string): Observable<boolean> {
        return this.store.select(fromTemplatesReducer.selectGroupNameExists, { name, groupId });
    }

    getTranslations$(): Observable<GetTranslationsDto> {
        return this.store.pipe(select(fromTemplatesReducer.selectGroupsTranslations));
    }

    dispatchGetTranslations(locales: string[], untranslatedOnly: boolean): void {
        this.store.dispatch(fromTemplatesActions.getTranslations({ locales, untranslatedOnly }));
    }

    dispatchUpdateTranslations(translations: UpdateTranslationsDto): void {
        this.store.dispatch(fromTemplatesActions.updateTranslations({ payload: translations }));
    }

    //Templates
    dispatchTemplates() {
        this.store.dispatch(fromTemplatesActions.getTemplates());
    }

    getTemplates$(): Observable<ITemplateEntityDTO[]> {
        return this.store.pipe(select(fromTemplatesReducer.selectAllTemplates));
    }

    dispatchTemplateByLegalFormAndAccountBasis(templateType: string, legalForm: string, accountBasis: string) {
        this.store.dispatch(
            fromTemplatesActions.getTemplateByLegalFormAndAccountBasis({ templateType, legalForm, accountBasis }),
        );
    }

    dispatchTemplateById(id: string) {
        this.store.dispatch(fromTemplatesActions.getTemplateById.do({ payload: id }));
    }

    getTemplateByLegalFormAndAccountBasis$(): Observable<ITemplateEntityDTO> {
        return this.store.pipe(select(fromTemplatesReducer.selectTemplateByLegalFormAndAccountBasis));
    }

    resetTemplateByLegalFormAndAccountBasis() {
        this.store.dispatch(fromTemplatesActions.resetTemplateByLegalFormAndAccountBasis());
    }

    getActiveTemplate$(): Observable<ITemplateEntityDTO> {
        return this.store.pipe(select(fromTemplatesReducer.selectActiveTemplate));
    }

    setActiveTemplate(templateId: string) {
        this.store.dispatch(fromTemplatesActions.setActiveTemplate({ payload: templateId }));
    }

    getActiveTemplateType$(): Observable<TemplateType> {
        return this.store.pipe(select(fromTemplatesReducer.selectActiveTemplateType));
    }

    setActiveTemplateType(templateType: TemplateType) {
        this.store.dispatch(fromTemplatesActions.setActiveTemplateType({ payload: templateType }));
    }

    removeActiveTemplate() {
        this.store.dispatch(fromTemplatesActions.removeActiveTemplate());
    }

    createTemplate(templateToBeCreated: ITemplateEntityDTO) {
        this.store.dispatch(fromTemplatesActions.createTemplate({ template: templateToBeCreated }));
    }

    updateTemplate(templateTobeUpdated: ITemplateEntityDTO, templateId: string) {
        this.store.dispatch(fromTemplatesActions.updateTemplate({ template: templateTobeUpdated, templateId }));
    }

    getTemplatesResponse$(): Observable<any> {
        return this.store.pipe(select(fromTemplatesReducer.selectTemplatesResponse));
    }

    removeTemplatesResponse() {
        this.store.dispatch(fromTemplatesActions.removeTemplatesResponse());
    }

    getTemplatesError$(): Observable<any> {
        return this.store.pipe(select(fromTemplatesReducer.selectTemplatesError));
    }

    removeTemplatesError() {
        this.store.dispatch(fromTemplatesActions.removeTemplatesError());
    }

    getTemplateNameExists$(name: string, templateId: string): Observable<boolean> {
        return this.store.select(fromTemplatesReducer.selectTemplateNameExists, { name, templateId });
    }

    getTemplateAccountBasisAndLegalFormExists$(
        accountBasis: string,
        legalForm: string,
        type: string,
        templateId: string,
    ): Observable<boolean> {
        return this.store.select(fromTemplatesReducer.selectTemplateAccountBasisAndLegalFormExists, {
            accountBasis,
            legalForm,
            type,
            templateId,
        });
    }

    // Templates Subtotals
    setSubtotalsNames(subtotalsNames: string[]) {
        this.store.dispatch(fromTemplatesActions.setSubtotalsNames({ payload: subtotalsNames }));
    }

    removeSubtotalName(subtotalName: string): void {
        this.store.dispatch(fromTemplatesActions.removeSubtotalName({ payload: subtotalName }));
    }

    getSubtotalNameExists$(name: string): Observable<boolean> {
        return this.store.select(fromTemplatesReducer.selectSubTotalNameExists, { name });
    }

    // Templates Flis
    setFinancialLineItems(financialLineItems: IFinancialLineItem[]) {
        this.store.dispatch(fromTemplatesActions.setFinancialLineItems({ payload: financialLineItems }));
    }

    getFinancialLineItems$(): Observable<IFinancialLineItem[]> {
        return this.store.pipe(select(fromTemplatesReducer.selectFinancialLineItems));
    }

    selectFinancialLineItems(financialLineItems: string[]): void {
        this.store.dispatch(fromTemplatesActions.selectFinancialLineItems({ payload: financialLineItems }));
    }

    deselectFinancialLineItem(financialLineItem: string): void {
        this.store.dispatch(fromTemplatesActions.deselectFinancialLineItem({ payload: financialLineItem }));
    }

    getCashAndProfitOptions$(): Observable<string[]> {
        return this.store.pipe(select(fromTemplatesReducer.selectCashAndProfitOptions));
    }

    getEquityGroupOptions$(): Observable<string[]> {
        return this.store.pipe(select(fromTemplatesReducer.selectEquityGroupOptions));
    }

    public getAllTemplatesNamesAndIdsByTemplateType$(): Observable<GetTemplatesNamesAndIdsDto[]> {
        return this.store.pipe(select(fromTemplatesReducer.SelectAllTemplatesNamesAndIdsByTemplateType));
    }

    public dispatchGetAllTemplatesNamesAndIdsByTemplateType(templateType: TemplateType): void {
        return this.store.dispatch(
            fromTemplatesActions.getAllTemplatesNamesAndIdsByTemplateType.getAllTemplatesNamesAndIdsByTemplateType({
                payload: templateType,
            }),
        );
    }

    dispatchGetTemplatesTargetTranslations(translationRequest: TemplatesTranslationRequest): void {
        return this.store.dispatch(
            fromTemplatesActions.getTemplatesTargetTranslations.do({
                payload: translationRequest,
            }),
        );
    }
}
